import { Project, ProjectSettingsModeGroupEnum, ProjectStateStatusEnum, Subproject } from '@/app/api';
import { useMemo } from 'react';
import { useIsSubprojectActive } from '@/app/pages/ProjectDetail/hooks/useIsSubprojectActive';
import { useTranslation } from 'react-i18next';
import { countryConfig } from '@/app/utils/countryConfig';
import { useIsProjectActive, useIsProjectInEndState } from '@/app/providers/ProjectProvider';
import { useIsUserInSubProjectBoxReplaceGroup } from '@/app/hooks/useIsUserInSubProjectBoxReplaceGroup';

export enum SideBarContentEnum {
  MESSAGES,
  REWARDS,
}

export interface ProjectDisplaySettings {
  isProjectClosedWidgetDisplayed: boolean;
  isGreenBackgroundDisplayed: boolean;
  isSubprojectBannerVisible: boolean;
  sideBarContent: SideBarContentEnum | null;
  footnoteText: string | null;
  moreProjectsHeading: string;
  isSupportProjectBannerVisible: boolean;
}

type Params = {
  project: Project;
  subproject?: Subproject;
};

const getSideBarContent = (donationsCount: number, isInEndState: boolean, isPlusProject: boolean) => {
  if (isPlusProject) {
    return SideBarContentEnum.REWARDS;
  }

  if (isInEndState || !donationsCount) {
    return null;
  }

  return SideBarContentEnum.MESSAGES;
};

export const useDisplaySettings = ({ project, subproject }: Params) => {
  const { t } = useTranslation();
  const isProjectActive = useIsProjectActive();
  const isUserInSubProjectBoxReplaceGroup = useIsUserInSubProjectBoxReplaceGroup();
  const isSubprojectActive = useIsSubprojectActive(subproject?.state.status);
  const isProjectInEndState = useIsProjectInEndState();
  const projectFootnoteText =
    project.settings?.modeGroup === ProjectSettingsModeGroupEnum.Plus
      ? t('pages.collection.footnoteForPlus')
      : t('pages.collection.footnote');

  const isSupportProjectBannerVisible =
    isUserInSubProjectBoxReplaceGroup && project.state?.status !== ProjectStateStatusEnum.Finished;

  const isSubprojectBannerVisible =
    !isUserInSubProjectBoxReplaceGroup &&
    project.settings?.modeGroup !== ProjectSettingsModeGroupEnum.Plus &&
    project.state?.status !== ProjectStateStatusEnum.Finished;

  const displaySettings: ProjectDisplaySettings = useMemo(() => {
    if (!subproject) {
      return {
        moreProjectsTitle:
          project.settings.modeGroup === ProjectSettingsModeGroupEnum.Plus
            ? t('pages.collection.moreCommercialProjects')
            : t('pages.collection.moreStories'),
        isProjectNewsVisible: true,
        isProjectClosedWidgetDisplayed: isProjectInEndState,
        isGreenBackgroundDisplayed: !!project.state?.actualSuccess,
        isSubprojectBannerVisible,
        isSupportProjectBannerVisible: isProjectActive && isSupportProjectBannerVisible,
        sideBarContent: getSideBarContent(
          project.statistics.donationsCount,
          isProjectInEndState,
          project.settings?.modeGroup === ProjectSettingsModeGroupEnum.Plus,
        ),
        footnoteText: countryConfig.shouldDisplayProjectFootnoteText ? projectFootnoteText : null,
        moreProjectsHeading:
          project.settings?.modeGroup === ProjectSettingsModeGroupEnum.Plus
            ? t('pages.collection.moreCommercialProjects')
            : t('pages.collection.moreStories'),
      };
    }

    return {
      moreProjectsTitle: t('pages.collection.moreStories'),
      isProjectNewsVisible: false,
      isProjectClosedWidgetDisplayed: isProjectInEndState,
      isSupportProjectBannerVisible: isSubprojectActive && isSupportProjectBannerVisible,

      // TODO what's the actual condition? Can subprojects have green background?
      // See https://doniocz.atlassian.net/browse/DON-2719
      isGreenBackgroundDisplayed: false,
      isSubprojectBannerVisible,
      sideBarContent: getSideBarContent(project.statistics.donationsCount, !isSubprojectActive, false),
      footnoteText: null,
      moreProjectsHeading:
        project.settings?.modeGroup === ProjectSettingsModeGroupEnum.Plus
          ? t('pages.collection.moreProjects')
          : t('pages.collection.moreStories'),
    };
  }, [project, isProjectActive, subproject, isSubprojectActive]);

  return displaySettings;
};
